import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Building2, Trees, ClipboardCheck, Phone, ChevronRight, ChevronLeft } from "lucide-react";

const slides = [
  {
    id: "bau",
    title: "Bauunternehmung",
    subtitle: "Bausanierung & Tiefbau",
    description: "Bausanierung, Kellerabdichtungen, Tiefbau und Pflasterarbeiten. Zuverlässig und in höchster Qualität.",
    features: ["Bausanierung", "Kellerabdichtungen", "Tiefbau", "Grundstücksentwässerung"],
    color: "blue",
    icon: Building2,
    link: "/bauunternehmung",
    cta: "Bauunternehmen entdecken",
    gradient: "from-[hsl(215,75%,15%)] via-[hsl(215,60%,10%)] to-[hsl(220,18%,7%)]",
    accentGradient: "gradient-blue",
    image: "/images/bau-hero.jpg",
  },
  {
    id: "galabau",
    title: "Galabau",
    subtitle: "Garten- & Landschaftsbau",
    description: "Grüne Räume mit Charakter. Von der Planung bis zur Pflege gestalten wir Außenanlagen, die begeistern und Wert schaffen.",
    features: ["Gartengestaltung", "Pflasterarbeiten", "Grünanlagenpflege", "Rasen & Rollrasen"],
    color: "green",
    icon: Trees,
    link: "/galabau",
    cta: "Galabau entdecken",
    gradient: "from-[hsl(152,40%,12%)] via-[hsl(152,30%,9%)] to-[hsl(220,18%,7%)]",
    accentGradient: "gradient-green",
    image: "/images/galabau-hero.jpg",
  },
  {
    id: "sachverstaendiger",
    title: "Sachverständiger",
    subtitle: "Gutachten & Beratung",
    description: "Unabhängige Expertise und fundierte Gutachten für Ihre Immobilien- und Bauprojekte. Gerichtsfest und zertifiziert.",
    features: ["Immobiliengutachten", "Bauschadenbewertung", "Wertermittlung", "Gerichtsfeste Gutachten"],
    color: "orange",
    icon: ClipboardCheck,
    link: "/sachverstaendiger",
    cta: "Sachverständiger kontaktieren",
    gradient: "from-[hsl(25,60%,12%)] via-[hsl(25,40%,9%)] to-[hsl(220,18%,7%)]",
    accentGradient: "gradient-orange",
    image: "/images/sv-hero.jpg",
  },
];

const colorClasses = {
  blue: {
    text: "text-brand-blue-light",
    border: "border-brand-blue/30",
    featureDot: "bg-brand-blue",
    indicator: "bg-brand-blue",
    indicatorInactive: "bg-brand-blue/30",
  },
  green: {
    text: "text-brand-green-light",
    border: "border-brand-green/30",
    featureDot: "bg-brand-green",
    indicator: "bg-brand-green",
    indicatorInactive: "bg-brand-green/30",
  },
  orange: {
    text: "text-brand-orange-light",
    border: "border-brand-orange/30",
    featureDot: "bg-brand-orange",
    indicator: "bg-brand-orange",
    indicatorInactive: "bg-brand-orange/30",
  },
};

export const HeroSection = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [direction, setDirection] = useState(1);

  const nextSlide = useCallback(() => {
    setDirection(1);
    setActiveSlide((prev) => (prev + 1) % slides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setDirection(-1);
    setActiveSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    const interval = setInterval(nextSlide, 6000);
    return () => clearInterval(interval);
  }, [nextSlide]);

  const current = slides[activeSlide];
  const colors = colorClasses[current.color];
  const Icon = current.icon;

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden" data-testid="hero-section">
      {/* Background image layer */}
      <AnimatePresence mode="wait">
        <motion.div
          key={current.id + "-bg"}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1, ease: [0.4, 0, 0.2, 1] }}
          className="absolute inset-0"
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${current.image})` }}
          />
          <div className={`absolute inset-0 bg-gradient-to-r ${current.gradient} opacity-75`} />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        </motion.div>
      </AnimatePresence>

      {/* Decorative grid lines */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-[1]">
        <div className="absolute top-0 left-1/4 w-px h-full bg-white/5" />
        <div className="absolute top-0 left-2/4 w-px h-full bg-white/5" />
        <div className="absolute top-0 left-3/4 w-px h-full bg-white/5" />
      </div>

      {/* Top bar */}
      <div className="relative z-10 pt-24 md:pt-28">
        <div className="container-wide">
          <div className="flex items-center gap-6 mb-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground font-body">
              <Phone className="w-3 h-3" />
              <span>+49 (0) 531 87 46 10</span>
            </div>
            <div className="w-px h-3 bg-border" />
            <span className="text-xs text-muted-foreground font-body">Raum Braunschweig</span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 container-wide flex-1 flex flex-col justify-center pt-6 pb-12 md:pt-10 md:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Static headline + CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className="flex gap-1 mb-8">
              <div className="w-12 h-1.5 gradient-blue" />
              <div className="w-12 h-1.5 gradient-green" />
              <div className="w-12 h-1.5 gradient-orange" />
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-tight text-foreground mb-6">
              Drei Kompetenzen.<br />
              <span className="text-muted-foreground">Eine starke Gruppe.</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground font-body max-w-xl leading-relaxed mb-8">
              Bauunternehmung, Garten- und Landschaftsbau sowie Sachverständigenwesen – 
              vereint unter einem Dach für Ihr Projekt.
            </p>
            <div className="flex flex-wrap gap-4 items-center">
              <Button variant="blue" size="xl" asChild>
                <Link to="/#kontakt">
                  Jetzt Projekt anfragen
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
              <Button variant="outline" size="xl" asChild>
                <a href="#leistungen">
                  Leistungen entdecken
                </a>
              </Button>
            </div>
          </motion.div>

          {/* Right: Slide content for current company */}
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, x: direction * 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: direction * -60 }}
              transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
              className="relative bg-card/60 backdrop-blur-md border border-white/10 p-8 md:p-10"
              data-testid="hero-slide-content"
            >
              {/* Accent top bar */}
              <div className={`absolute top-0 left-0 right-0 h-1 ${current.accentGradient}`} />

              <div className="flex items-center gap-3 mb-5">
                <div className={`p-2.5 border ${colors.border} bg-black/20`}>
                  <Icon className={`w-5 h-5 ${colors.text}`} />
                </div>
                <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground">
                  {current.subtitle}
                </span>
              </div>

              <h2 className={`font-heading font-bold text-2xl md:text-3xl mb-3 ${colors.text}`}>
                {current.title}
              </h2>

              <p className="text-sm text-muted-foreground font-body leading-relaxed mb-6">
                {current.description}
              </p>

              {/* Feature list */}
              <div className="grid grid-cols-2 gap-2 mb-8">
                {current.features.map((feature) => (
                  <div key={feature} className="flex items-center gap-2.5">
                    <span className={`w-1.5 h-1.5 ${colors.featureDot} shrink-0`} />
                    <span className="text-xs text-foreground/80 font-body">{feature}</span>
                  </div>
                ))}
              </div>

              <Button
                variant={`${current.color}-outline`}
                size="default"
                className="group/btn"
                asChild
              >
                <Link to={current.link}>
                  {current.cta}
                  <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1" style={{ transition: 'transform 0.2s' }} />
                </Link>
              </Button>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Slider controls + indicators */}
      <div className="relative z-10 border-t border-white/10 bg-black/30 backdrop-blur-sm">
        <div className="container-wide py-5">
          <div className="flex items-center justify-between">
            {/* Slide indicators */}
            <div className="flex items-center gap-3">
              {slides.map((slide, index) => {
                const slideColors = colorClasses[slide.color];
                return (
                  <button
                    key={slide.id}
                    onClick={() => {
                      setDirection(index > activeSlide ? 1 : -1);
                      setActiveSlide(index);
                    }}
                    className={`flex items-center gap-2 px-3 py-2 border transition-all duration-300 ${
                      index === activeSlide
                        ? `${slideColors.border} bg-white/5`
                        : "border-transparent hover:border-white/10"
                    }`}
                    data-testid={`hero-slide-indicator-${slide.id}`}
                  >
                    <div className={`w-2 h-2 transition-all duration-300 ${
                      index === activeSlide ? slideColors.indicator : "bg-white/20"
                    }`} />
                    <span className={`text-xs font-heading uppercase tracking-wider transition-colors duration-300 ${
                      index === activeSlide ? slideColors.text : "text-muted-foreground"
                    }`}>
                      {slide.title}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Navigation arrows */}
            <div className="flex items-center gap-2">
              <button
                onClick={prevSlide}
                className="p-2 border border-white/10 hover:border-white/30 hover:bg-white/5 transition-all"
                data-testid="hero-slide-prev"
              >
                <ChevronLeft className="w-4 h-4 text-foreground" />
              </button>
              <button
                onClick={nextSlide}
                className="p-2 border border-white/10 hover:border-white/30 hover:bg-white/5 transition-all"
                data-testid="hero-slide-next"
              >
                <ChevronRight className="w-4 h-4 text-foreground" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="relative z-10 border-t border-border bg-card/80 backdrop-blur-md">
        <div className="container-wide py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "25+", label: "Jahre Erfahrung" },
              { value: "500+", label: "Projekte realisiert" },
              { value: "3", label: "Fachunternehmen" },
              { value: "100%", label: "Kundenzufriedenheit" },
            ].map((stat) => (
              <div key={stat.label} className="text-center md:text-left">
                <p className="font-heading font-bold text-2xl md:text-3xl text-foreground">{stat.value}</p>
                <p className="text-xs text-muted-foreground font-body mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
